import React, { useContext, useMemo } from 'react';
import Loading from 'components/Loading';
import ErrorBoundary from 'components/Common/ErrorBoundary';
import SelectedEntityProvider from './SelectedEntityProvider';
import styled from 'styled-components';
import { CLOSED_NAV_WIDTH, OPENED_NAV_WIDTH } from '../../navigation/styles';
import NavSideBarContext from '../../contexts/NavSideBarContext';
import EntityDetailsContext from './EntityDetailsContext';
import TopBar from './TopBar';
import Dashboard from '../../components/Dashboard';
import { DASHBOARD_MENU_PORTAL_DIV_ID } from '../DashboardShow/constants';
import { TABS_TOP_BAR_HEIGHT, Z_INDEX } from '../../constants';
import isPerformanceBoard from '../../isPerformanceBoard';
import PeriodsProvider from '../../contextProviders/BoardPeriodsProvider';
import PerformanceBoards from '../../components/PerformanceBoards';
import useIsEngagementMode from '../../hooks/useIsEngagementMode';
import Report from '../../components/Report';
import isDriverPerformanceBoard from '../../isDriverPerformanceBoard';
import Board from '../../components/Board';
import isGeneralBoard from '../../isBoardWithSlideOut';
import entityDetailsClientTabTypeCheckers from './entityDetailsClientTabTypeChckers';
import DriverDetailsTab from './DriverDetailsTab';
import MappedUserContentSettingsProvider, {
  MappedUserContentSettingsContext,
} from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/MappedUserContentSettingsProvider';
import CustomerLaneCommitBoard from '../../components/CustomerLaneCommitsBoard';
import isCustomerLaneBoard from '../../isCustomerLaneBoard';
import useSelectedEntityDefinition from './SelectedEntityProvider/useSelectedEntityDefinition';
import { EngagementPortalsContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider/EngagementPortalsProvider';
import { UIVersionContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import DriverBonusOverview from '../../components/DriverAX/screens/DriverBonusOverview';
import DriverBonusOverviewCombined from '../../components/DriverAX/screens/DriverBonusOverview/DriverBonusOverviewCombined';
import Scorecard from '../../components/Scorecard';
import PopupProvider from '../../contextProviders/PopupProvider';

const FullHeightContainer = styled.div`
  height: calc(100vh - 200px);
  overflow-y: scroll;
`;

const FullHeightFlexContainer = styled.div`
  height: calc(100vh - 200px);
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
`;

const GridContainer = styled.div`
  height: calc(100vh - ${TABS_TOP_BAR_HEIGHT}px - 80px);
`;
const ScrollingGridContainer = styled.div`
  height: calc(100vh - ${TABS_TOP_BAR_HEIGHT}px);
  overflow-y: scroll;
  display: flex;
  flex: 1;
`;

const EntityAppDiv = styled.div<{
  isNavOpen: boolean;
  isFullWidth: boolean;
}>`
  height: 100%;
  width: ${(props) =>
    props.isFullWidth
      ? '100%'
      : props.isNavOpen
        ? `calc(100% - ${OPENED_NAV_WIDTH}px)`
        : `calc(100% - ${CLOSED_NAV_WIDTH}px)`};
  display: flex;
  flex-direction: column;
`;

export const ShowTab = ({ tab }: { tab?: EntityDetails.ClientTab }) => {
  let component: JSX.Element | null = null;
  if (tab === undefined) {
    component = null;
  } else if (entityDetailsClientTabTypeCheckers.isDashboardTab(tab)) {
    component = (
      <>
        <GridContainer>
          <div
            id={DASHBOARD_MENU_PORTAL_DIV_ID}
            style={{
              position: 'absolute',
              right: 24,
              top: TABS_TOP_BAR_HEIGHT + 11,
              zIndex: Z_INDEX.WORKSPACE_DASHBOARD_MENU,
            }}
          />
          <Dashboard key={tab.dashboard.id} dashboard={tab.dashboard} />
        </GridContainer>
      </>
    );
  } else if (entityDetailsClientTabTypeCheckers.isScorecardTab(tab)) {
    component = (
      <ErrorBoundary>
        <PopupProvider>
          <FullHeightFlexContainer>
            <Scorecard
              scorecard={tab.scorecard}
              key={tab.scorecard.id}
              isAddKpiEnabled={false}
            />
          </FullHeightFlexContainer>
        </PopupProvider>
      </ErrorBoundary>
    );
  } else if (entityDetailsClientTabTypeCheckers.isBoardTab(tab)) {
    if (isPerformanceBoard(tab.board)) {
      component = (
        <GridContainer>
          <PeriodsProvider board={tab.board}>
            <PerformanceBoards.Board key={tab.board.id} board={tab.board} />
          </PeriodsProvider>
        </GridContainer>
      );
    }
    if (isDriverPerformanceBoard(tab.board) || isGeneralBoard(tab.board)) {
      component = (
        <GridContainer>
          <PeriodsProvider board={tab.board}>
            <Board
              key={tab.board.id}
              board={tab.board}
              initialRunTimeFilters={window.emptyArray}
              initialScopeFilters={window.emptyArray}
            />
          </PeriodsProvider>
        </GridContainer>
      );
    }
    if (isCustomerLaneBoard(tab.board)) {
      component = (
        <GridContainer>
          <CustomerLaneCommitBoard board={tab.board} isDeletable={false} />
        </GridContainer>
      );
    }
  } else if (entityDetailsClientTabTypeCheckers.isReportTab(tab)) {
    component = (
      <ScrollingGridContainer>
        <Report key={tab.report.id} report={tab.report} />
      </ScrollingGridContainer>
    );
  } else if (
    entityDetailsClientTabTypeCheckers.isDriverBonusProgramReportTab(tab)
  ) {
    component = (
      <ErrorBoundary>
        {/* Key by program id (like the dashboard/board/report/scorecard tabs above) so switching
            between bonus programs remounts rather than reusing one instance — otherwise the
            per-program period state in BonusProgramPeriodsProvider persists across the swap and the
            new program fetches with the previous program's period id. */}
        <DriverBonusOverview
          key={tab.bonusProgram.id}
          program={tab.bonusProgram}
        />
      </ErrorBoundary>
    );
  } else if (entityDetailsClientTabTypeCheckers.isDriverBonusCombinedTab(tab)) {
    component = (
      <ErrorBoundary>
        <DriverBonusOverviewCombined />
      </ErrorBoundary>
    );
  } else if (entityDetailsClientTabTypeCheckers.isDriverDetailsTab(tab)) {
    component = (
      <ErrorBoundary>
        <FullHeightContainer>
          <DriverDetailsTab />
        </FullHeightContainer>
      </ErrorBoundary>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        height:
          tab &&
          (entityDetailsClientTabTypeCheckers.isDriverBonusProgramReportTab(
            tab,
          ) ||
            entityDetailsClientTabTypeCheckers.isDriverBonusCombinedTab(tab))
            ? '100%'
            : undefined,
        overflow: 'hidden',
      }}
    >
      {component}
    </div>
  );
};

export const EntityDetailsShowContainer = () => {
  const { isLoadingMappedUsers } = useContext(MappedUserContentSettingsContext);
  const entityDetails = useContext(EntityDetailsContext);
  const { isOpen: isNavOpen } = useContext(NavSideBarContext);
  const { isEngagementMode } = useIsEngagementMode();
  const { isV3 } = useContext(UIVersionContext);

  if (!entityDetails) {
    return <Loading />;
  }
  return (
    <EntityAppDiv isNavOpen={isNavOpen} isFullWidth={isEngagementMode || isV3}>
      <TopBar />
      {!isLoadingMappedUsers && <ShowTab tab={entityDetails.selectedTab} />}
      {isLoadingMappedUsers && <Loading />}
    </EntityAppDiv>
  );
};

const Gate = ({ isEngagementMode }: { isEngagementMode?: boolean }) => {
  const entityDefinition = useSelectedEntityDefinition();

  const { allEngagementPortals } = useContext(EngagementPortalsContext);
  const mappedUserPortal = useMemo(() => {
    if (!entityDefinition) {
      return undefined;
    }

    return allEngagementPortals.find((p) => {
      if (p.entityLinks.length === 0) {
        return false;
      }
      return p.entityLinks.some(
        (l) => l.entityId === entityDefinition.entity.id,
      );
    });
  }, [allEngagementPortals, entityDefinition]);

  if (!entityDefinition) {
    return <Loading />;
  }

  return (
    <ErrorBoundary>
      <MappedUserContentSettingsProvider
        entity={entityDefinition.entity}
        entityAppId={entityDefinition.app.id}
        mappedUserPortal={isEngagementMode ? mappedUserPortal : undefined}
      >
        <SelectedEntityProvider
          entity={entityDefinition.entity}
          entityApp={entityDefinition.app}
        >
          <EntityDetailsShowContainer />
        </SelectedEntityProvider>
      </MappedUserContentSettingsProvider>
    </ErrorBoundary>
  );
};

export default Gate;
